/**
 * ustvaXmlApi — FE-Client für `POST /ustva/generate-xml` (M-U9, invoice-service).
 *
 * Der Endpoint liefert bei Erfolg die ELSTER-Nutzdaten-XML als `application/xml`
 * (ISO-8859-15). Der Body wird AUSSCHLIESSLICH als `response.blob()` gelesen und als
 * Download getriggert (Muster `datevBelegeApi.ts` — NIEMALS `response.text()`, das würde
 * das ISO-8859-15-Encoding re-interpretieren und die Bytes zerstören).
 *
 * Bei 422 liefert der Server eine typisierte Fehlerliste (`§5.4`-Codes), die das Panel
 * auf i18n-Meldungen mappt. 401/403/429/Netzfehler werden zu diskriminierten Ergebnis-
 * Varianten — der Aufrufer zeigt sie ohne den Download auszulösen (fail-closed).
 *
 * @module ustvaXmlApi
 */

import { apiRequest } from './apiRequest';
import logger from '../utils/logger';
import type { UstvaErrorCode, UstvaGenerateXmlRequest } from '../utils/ustvaExport';

/** Eine einzelne Server-Fehlermeldung (§5.2, 422-Body). */
export interface UstvaError {
  source: 'gate' | 'invariant' | 'steuernummer' | 'structural' | 'xsd' | string;
  code: UstvaErrorCode;
  /** Debug-Text (de) — NICHT für die UI-Anzeige; das Panel mappt `code` → i18n. */
  message: string;
  line: number | null;
}

/** Validierungsmodus des Servers (Header `X-Ustva-Validation`, §5.2). */
export type UstvaValidationMode = 'xsd' | 'structural';

/** Diskriminiertes Ergebnis des Export-Aufrufs. */
export type UstvaXmlResult =
  | {
      kind: 'success';
      /** Wie der Server validiert hat (XSD vs. struktureller Fallback), falls Header gesetzt. */
      validation: UstvaValidationMode | null;
      /** true ⇒ Sonderzeichen wurden transliteriert (Header `X-Ustva-Translit: 1`). */
      translit: boolean;
      /** Effektiver Download-Dateiname (aus Content-Disposition oder Fallback). */
      filename: string;
    }
  | { kind: 'validation_error'; errors: UstvaError[] }
  | { kind: 'auth_error' }
  | { kind: 'rate_limited' }
  | { kind: 'network_error' }
  | { kind: 'server_error'; status: number };

const USTVA_XML_ENDPOINT = '/ustva/generate-xml';

/** Ableitbarer Fallback-Dateiname `UStVA_<jahr>_<M01|Q1..Q4>.xml` aus dem Request (§5.2). */
function fallbackFilename(request: UstvaGenerateXmlRequest): string {
  const { jahr, zeitraumCode } = request.period;
  let suffix = zeitraumCode;
  if (/^\d{2}$/.test(zeitraumCode)) {
    const n = Number.parseInt(zeitraumCode, 10);
    // 01..12 = Monat → "M01".."M12"; 41..44 = Quartal → "Q1".."Q4".
    suffix = n >= 41 && n <= 44 ? `Q${n - 40}` : `M${zeitraumCode}`;
  }
  return `UStVA_${jahr}_${suffix}.xml`;
}

/** Extrahiert den Dateinamen aus `Content-Disposition`, sonst `fallback`. */
function extractFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim() || fallback;
}

/** Triggert den Browser-Download eines Blobs via Object-URL `<a download>`. */
function triggerXmlDownload(blob: Blob, fileName: string): void {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
}

/** Parst den 422-Body defensiv zu einer `UstvaError[]`. */
async function parseValidationErrors(response: Response): Promise<UstvaError[]> {
  try {
    const body = (await response.json()) as { errors?: unknown } | null;
    if (body && Array.isArray(body.errors)) {
      return body.errors
        .filter((e): e is Record<string, unknown> => e != null && typeof e === 'object')
        .map((e) => ({
          source: typeof e.source === 'string' ? e.source : 'invariant',
          code: e.code as UstvaErrorCode,
          message: typeof e.message === 'string' ? e.message : '',
          line: typeof e.line === 'number' ? e.line : null,
        }));
    }
  } catch (err) {
    logger.error('ustvaXmlApi: 422 body not parseable as JSON', err);
  }
  return [];
}

/**
 * Ruft `POST /ustva/generate-xml` auf und triggert bei Erfolg den XML-Download.
 *
 * Wirft NIE — jeder Fehlerpfad wird zu einer `UstvaXmlResult`-Variante (der Aufrufer
 * mappt sie auf UI-Meldungen). Der Download läuft ausschließlich bei `response.ok`.
 *
 * @param request Der vertragskonforme Payload (`toUstvaXmlRequest`).
 * @returns Diskriminiertes Ergebnis (`success` | `validation_error` | `auth_error`
 *          | `rate_limited` | `network_error` | `server_error`).
 */
export async function generateUstvaXml(
  request: UstvaGenerateXmlRequest,
): Promise<UstvaXmlResult> {
  let response: Response;
  try {
    response = await apiRequest(USTVA_XML_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  } catch (networkErr) {
    logger.error('ustvaXmlApi: network error', networkErr);
    return { kind: 'network_error' };
  }

  if (response.ok) {
    try {
      const blob = await response.blob();
      const fileName = extractFilename(
        response.headers.get('Content-Disposition'),
        fallbackFilename(request),
      );
      triggerXmlDownload(blob, fileName);
      const validationRaw = response.headers.get('X-Ustva-Validation');
      const validation: UstvaValidationMode | null =
        validationRaw === 'xsd' || validationRaw === 'structural' ? validationRaw : null;
      return {
        kind: 'success',
        validation,
        translit: response.headers.get('X-Ustva-Translit') === '1',
        filename: fileName,
      };
    } catch (blobErr) {
      logger.error('ustvaXmlApi: reading/downloading blob failed', blobErr);
      return { kind: 'network_error' };
    }
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'auth_error' };
  }
  if (response.status === 429) {
    return { kind: 'rate_limited' };
  }
  if (response.status === 422) {
    return { kind: 'validation_error', errors: await parseValidationErrors(response) };
  }
  logger.error('ustvaXmlApi: unexpected status', { status: response.status });
  return { kind: 'server_error', status: response.status };
}
